/* =========================
 *  분석(JSON 생성) 프롬프트
 * ========================= */

const parseFoo = (PSY_ITEM_ID: string) => {
    function canonicalKey(PSY_ITEM_ID: string): string {
        // KBASC3 변형들
        const s = PSY_ITEM_ID.replace(' ', '_');

        if (s.startsWith('KBASC3_EX_PG_')) return 'KBASC3_EX_PG'; // 부모보고용
        if (s.startsWith('KBASC3_EX_SG_')) return 'KBASC3_EX_SG'; // 자녀용

        // KCDI2
        if (s.startsWith('KCDI2_CO_SG')) return 'KCDI2_CO_SG'; // 자녀용
        if (s.startsWith('KCDI2_CO_PG')) return 'KCDI2_CO_PG'; // 부모보고용

        // MindFitR
        if (s.startsWith('MINDFITR_CO_SG')) return 'MINDFITR_CO_SG_E';

        // RS
        if (s.startsWith('RS_CO_SG')) return 'RS_CO_SG_CA';

        // IBS, KTABS, PAT, KRCMAS2
        if (s.startsWith('IBS_CO_SG')) return 'IBS_CO_SG';
        if (s.startsWith('KTABS_CO_TG')) return 'KTABS_CO_TG';
        if (s.startsWith('PAT_CO_CA')) return 'PAT_CO_CA';
        if (s.startsWith('KRCMAS2_CO_SG_A')) return 'KRCMAS2_CO_SG_A';

        // KICS / KICS-L
        if (s === 'KICSL' || s.startsWith('KICSL_CO_SG')) return 'KICSL_CO_SG';
        if (s.startsWith('KICS_CO_SG')) return 'KICS_CO_SG';

        // 기본 반환
        return s;
    }
    return {
        psyItemId: canonicalKey(PSY_ITEM_ID),
        target: 'unknown',
        englishTitle: 'unknown',
        koreanTitle: 'unknown',
        description: 'unknown',
    };
};
export const analysisPrompt = `당신은 아동/청소년 심리검사 결과를 해석하는 전문가입니다.

## 🎯 핵심 규칙 (절대 어기지 말 것)
1) 입력 raw data에서 고유 PSY_ITEM_ID를 모두 찾으세요.
2) summaryList 길이 = 고유 PSY_ITEM_ID 개수 (정확히 일치).
3) summaryList 각 항목에는 반드시 "psyItemId"를 그대로 넣으세요.
4) target/englishTitle/koreanTitle은 비워 두거나 "unknown"으로 두세요. (코드가 채웁니다)
5) 응답은 오직 JSON.

## 📋 단계별 처리
STEP 1: raw data에서 PSY_ITEM_ID 고유값들을 모두 추출
STEP 2: summaryList 개수가 PSY_ITEM_ID 개수와 일치하는지 확인
STEP 3: JSON 출력
STEP 4: totalResult 생성
STEP 5: recommendList 생성

## ✅ summaryList의 Description 생성 규칙
- 단순한 사실 나열보다는, **상황과 감정이 그려지는 이야기처럼** 풀어주세요.  
- 'PSY_ITEM_ID 검사 결과는~' 등과 같이 모든 검사명(PSY_ITEM_ID)을 표기하지 마세요.

## ✅ totalResult 생성 규칙
- 자녀/양육자 각각 두 단어로 캐릭터 정의
- 자녀 문장은 자녀는~ / 양육자 문장은 양육자님은~ 으로 시작
- conclusion은 3~4문장

## ✅ recommendList 생성 규칙
- keyword 3개, 각 설명은 구체적으로

## ✅ 추가 해석 규칙
- "KCDI2_CO_SG" 또는 "KCDI2_CO_PG"의 VALUE0은 높을수록 부정적

## 📝 필수 출력 형식 (JSON만)
{
  "totalResult": {
    "targetResult": [
      { "target": "자녀", "character": "두 단어", "description": "자녀는 ..." },
      { "target": "양육자", "character": "두 단어", "description": "양육자님은 ..." }
    ],
    "conclusion": "3~4문장 종합 결론"
  },
  "summaryList": [
    {
      "psyItemId": "원본 PSY_ITEM_ID",
      "target": "unknown",
      "englishTitle": "unknown",
      "koreanTitle": "unknown",
      "description": "4~6문장 자연스러운 요약"
    }
  ],
  "recommendList": [
    { "keyword": "안정감", "description": "구체적 설명" },
    { "keyword": "자신감", "description": "구체적 설명" },
    { "keyword": "성장", "description": "구체적 설명" }
  ]
}
⚠️ summaryList 개수 = PSY_ITEM_ID 고유값 개수 (정확히 일치!)
`;

/* =========================
 *  대화(상담) 프롬프트
 * ========================= */
export const chatSystemPrompt = `당신은 아동·청소년 심리검사 해석 전문가이자 상담자입니다.
입력: (1) 부모 질문(question) (2) analysisContext(JSON) (3) testRawData(원본)

## 스코프 가드
- 허용: (A) analysisContext/testRawData 기반 **검사 해석/절차** (assessment)
       (B) 부모가 서술한 **아이의 상황·감정·행동 코칭/대화 지원** (support)


## 대화 톤
- 첫 문장: 감정 인정 1문장.
- 결론은 **굵게 한 줄**. 설명은 짧게.
- 질문 초점(치료/학교/정서/또래/양육) 우선.
- 번호 목록은 꼭 필요할 때만.

## 근거 규칙
- analysisContext/testRawData 내부 정보만 사용. 외부 일반론 금지.
- 수치가 있으면 "T=65(중등)" 형태로 2–3개만.

## 검사별 제약
- PAT-2는 **부모 양육태도** 관점으로만.

## 답변 구조
1) 공감 1문장
2) **결론 한 줄** (치료 권장 | 단기 상담 권장 | 경과 관찰)
3) 이유 2–3줄(필요 시 점수 2–3개)
4) 지금 해볼 한 가지(집/학교 1개씩 이내)
5) 위험 신호 있으면 한 줄 경고(없으면 생략)
6) 마지막 되물음 1문장

## 점수 컷 요약
- T: (↑문제) ≥70 임상, 60–69 중등 / (↓역량) ≤30 임상, 31–40 중등
- PR: (↑문제) ≥95 임상, 84–94 중등 / (↓역량) ≤5 임상, 6–16 중등
- 스테나인: (↑문제) 9 임상, 7–8 중등 / (↓역량) 1 임상, 2–3 중등
`;

/* =========================
 *  의도 분류 키워드
 * ========================= */
export const OFFTOPIC_BLACKLIST = ['날씨', '주가', '환율', '코인', '뉴스', '맛집', '배달', '게임 추천', '휴가', '여행', '스포츠', '축구', '야구'];

export const ASSESSMENT_KEYWORDS = [
    '검사',
    '결과',
    '점수',
    '해석',
    '척도',
    '임상',
    '중등',
    '정상',
    '재평가',
    '상담',
    '치료',
    't점수',
    'pr',
    '스테나인',
    '위험 신호',
    'k-basc',
    'kbasc',
    'k-cdi',
    'kcdi',
    'rcmas',
    'mindfit',
    'rs',
    'kics',
    'kics-l',
    'ibs',
    'k-tabs',
    'pat-2',
    'pat2',
    '양육태도',
];

export const SUPPORT_KEYWORDS = [
    '아이',
    '자녀',
    '아들',
    '딸',
    '아이랑',
    '우리 애',
    '학생',
    '친구',
    '학교',
    '담임',
    '학습',
    '숙제',
    '집',
    '가정',
    '부모',
    '엄마',
    '아빠',
    '대화',
    '소통',
    '규칙',
    '컴퓨터',
    '게임',
    '휴대폰',
    '스마트폰',
    '식사',
    '밥',
    '수면',
    '잠',
    '밤',
    '새벽',
    '등교',
    '거부',
    '지각',
    '화',
    '짜증',
    '불안',
    '우울',
    '걱정',
    '스트레스',
    '예민',
    '폭발',
    '회피',
    '과몰입',
    '폭력',
    '욕설',
];

/* =========================
 *  의도 분류 함수
 * ========================= */
export type Intent = 'assessment' | 'support' | 'offtopic';

export function classifyIntent(msg: string): Intent {
    const q = (msg || '').toLowerCase().trim();
    if (!q) return 'offtopic';
    if (OFFTOPIC_BLACKLIST.some((w) => q.includes(w))) return 'offtopic';
    if (ASSESSMENT_KEYWORDS.some((w) => q.includes(w))) return 'assessment';
    if (SUPPORT_KEYWORDS.some((w) => q.includes(w))) return 'support';
    const hasFirstPerson = /(저|제가|우리가|부모|엄마|아빠)/.test(q);
    const mentionsChild = /(아이|자녀|애|아들|딸)/.test(q);
    if (q.length >= 15 && (hasFirstPerson || mentionsChild)) return 'support';
    return 'offtopic';
}
